import { z } from 'zod/v4';

import { CurrencyRuntype } from '../backendTypes';
import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import {
    CalculationExportRuntype,
    CreateFlexibleCalculationPlanFromTemplateRuntype,
    CreateFlexibleCalculationPlanRuntype,
    EvaluateCalculationRequestRuntype,
    EvaluatedCalculationRuntype,
    ExportedVariableInputRuntype,
    ExportedVariableRuntype,
    ExportedVariableUpdateRuntype,
    FlexibleCalculationKeyValueInputRuntype,
    FlexibleCalculationKeyValueRuntype,
    FlexibleCalculationKeyValueUpdateRuntype,
    FlexibleCalculationPlanRuntype,
    FlexibleCalculationTemplateInputRuntype,
    FlexibleCalculationTemplateRuntype,
    FlexibleCalculationTemplateUpdateRuntype,
    UpdateFlexibleCalculationPlanRuntype,
    ValidateCalculationPlanResponseRuntype,
    ValidateCalculationTemplateResponseRuntype,
} from './flexibleCalculationBackendTypes';

const EXPORTED_VARIABLES_KEY = 'flexible-calculation-exported-variables';

export const flexibleCalculationEndpoints = {
    'POST /flexible-calculation/evaluate': endpoint({
        description: 'Evaluate a calculation plan and return the computed values',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: EvaluateCalculationRequestRuntype,
        responseBody: EvaluatedCalculationRuntype,
    }),

    'GET /flexible-calculation/kv/:key': endpoint({
        description: 'Retrieve a single key-value pair by key',
        pathParams: z.object({
            key: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: FlexibleCalculationKeyValueRuntype,
        actionPermission: ({ key }: { key: string }) =>
            key === EXPORTED_VARIABLES_KEY ? 'CanViewCalculationTemplate' : 'CanViewCalculationForRfQ',
    }),

    'POST /flexible-calculation/kv': endpoint({
        description: 'Create a new key-value pair (returns 409 if key exists)',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: FlexibleCalculationKeyValueInputRuntype,
        responseBody: FlexibleCalculationKeyValueRuntype,
        invalidates: ['GET /flexible-calculation/kv/:key'],
        actionPermission: ({ key }: { key: string }) =>
            key === EXPORTED_VARIABLES_KEY ? 'CanCreateCalculationTemplate' : 'CanCreateCalculationForRfQ',
    }),

    'PATCH /flexible-calculation/kv/:key': endpoint({
        description: 'Update the value for an existing key-value pair',
        pathParams: z.object({
            key: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: FlexibleCalculationKeyValueUpdateRuntype,
        responseBody: FlexibleCalculationKeyValueRuntype,
        invalidates: ['GET /flexible-calculation/kv/:key'],
        actionPermission: ({ key }: { key: string }) =>
            key === EXPORTED_VARIABLES_KEY ? 'CanUpdateCalculationTemplate' : 'CanUpdateCalculationForRfQ',
    }),

    'DELETE /flexible-calculation/kv/:key': endpoint({
        description: 'Delete a key-value pair by key',
        pathParams: z.object({
            key: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.union([z.undefined(), z.literal('')]),
        invalidates: ['GET /flexible-calculation/kv/:key'],
        actionPermission: ({ key }: { key: string }) =>
            key === EXPORTED_VARIABLES_KEY ? 'CanDeleteCalculationTemplate' : 'CanDeleteCalculationForRfQ',
    }),

    'POST /flexible-calculation/plans': endpoint({
        description: 'Create a new calculation plan',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: CreateFlexibleCalculationPlanRuntype,
        responseBody: FlexibleCalculationPlanRuntype,
        invalidates: ['GET /flexible-calculation/plans/:id', 'GET /flexible-calculation/plans/rfq/:rfqId'],
        actionPermission: () => 'CanCreateCalculationForRfQ',
    }),

    'POST /flexible-calculation/plans/from-template': endpoint({
        description: 'Create a new calculation plan from a template (backend runs the template->plan transformation)',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: CreateFlexibleCalculationPlanFromTemplateRuntype,
        responseBody: FlexibleCalculationPlanRuntype,
        invalidates: ['GET /flexible-calculation/plans/:id', 'GET /flexible-calculation/plans/rfq/:rfqId'],
        actionPermission: () => 'CanCreateCalculationForRfQ',
    }),

    'GET /flexible-calculation/plans/:id': endpoint({
        description: 'Retrieve a calculation plan by ID',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: FlexibleCalculationPlanRuntype,
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),

    'GET /flexible-calculation/plans/:id/export': endpoint({
        description:
            "What the calculation's export shows: the evaluation (frozen or fresh) with the project, the main assembly, and each scenario's order size, batch sizes and lead times",
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: CalculationExportRuntype,
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),

    'GET /flexible-calculation/plans/rfq/:rfqId': endpoint({
        description: 'Retrieve all calculation plans for an RFQ',
        pathParams: z.object({
            rfqId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(FlexibleCalculationPlanRuntype),
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),

    'PATCH /flexible-calculation/plans/:id': endpoint({
        description:
            'Update a calculation plan. Keeps the error body on the HttpError it throws, because the "Ask for help with AI" action on a failed save prompts the model with it and a bare error code leaves nothing to diagnose.',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: UpdateFlexibleCalculationPlanRuntype,
        responseBody: FlexibleCalculationPlanRuntype,
        handleResponse: async (response, registeredEndpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, registeredEndpoint, {
                attachResponseBody: true,
            });
            // `http` checks the shape against `responseBody` after this handler returns.
            return (await response.json()) as z.infer<typeof FlexibleCalculationPlanRuntype>;
        },
        invalidates: [
            'GET /flexible-calculation/plans/:id',
            'GET /flexible-calculation/plans/rfq/:rfqId',
            'POST /flexible-calculation/evaluate',
            'GET /flexible-calculation/plans/:id/export',
        ],
        actionPermission: () => 'CanUpdateCalculationForRfQ',
    }),

    'PATCH /flexible-calculation/plans/:id/approve-and-freeze': endpoint({
        description: 'Approves and freezes a calculation plan',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.union([z.undefined(), z.literal('')]),
        invalidates: [
            'GET /flexible-calculation/plans/:id',
            'GET /flexible-calculation/plans/rfq/:rfqId',
            'POST /flexible-calculation/evaluate',
            'GET /flexible-calculation/plans/:id/export',
        ],
        actionPermission: () => 'CanUpdateCalculationForRfQ',
    }),

    'POST /flexible-calculation/plans/validate': endpoint({
        description: 'Validate a calculation plan for structural errors',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: FlexibleCalculationPlanRuntype,
        responseBody: ValidateCalculationPlanResponseRuntype,
        actionPermission: () => 'CanViewCalculationForRfQ',
    }),

    'DELETE /flexible-calculation/plans/:id': endpoint({
        description: 'Delete a calculation plan',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.union([z.undefined(), z.literal('')]),
        invalidates: ['GET /flexible-calculation/plans/:id', 'GET /flexible-calculation/plans/rfq/:rfqId'],
        actionPermission: () => 'CanDeleteCalculationForRfQ',
    }),

    // Template REST API endpoints
    'GET /flexible-calculation/templates': endpoint({
        description: 'Retrieve all calculation templates',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(FlexibleCalculationTemplateRuntype),
        actionPermission: () => 'CanViewCalculationTemplate',
    }),

    'GET /flexible-calculation/templates/:id': endpoint({
        description: 'Retrieve a calculation template by ID',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: FlexibleCalculationTemplateRuntype,
        actionPermission: () => 'CanViewCalculationTemplate',
    }),

    'POST /flexible-calculation/templates': endpoint({
        description: 'Create a new calculation template',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: FlexibleCalculationTemplateInputRuntype,
        responseBody: FlexibleCalculationTemplateRuntype,
        invalidates: ['GET /flexible-calculation/templates', 'GET /flexible-calculation/templates/:id'],
        actionPermission: () => 'CanCreateCalculationTemplate',
    }),

    'PATCH /flexible-calculation/templates/:id': endpoint({
        description: 'Update a calculation template',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: FlexibleCalculationTemplateUpdateRuntype,
        responseBody: FlexibleCalculationTemplateRuntype,
        invalidates: ['GET /flexible-calculation/templates', 'GET /flexible-calculation/templates/:id'],
        actionPermission: () => 'CanUpdateCalculationTemplate',
    }),

    'POST /flexible-calculation/templates/validate': endpoint({
        description: 'Validate a calculation template for structural errors',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: FlexibleCalculationTemplateRuntype,
        responseBody: ValidateCalculationTemplateResponseRuntype,
        actionPermission: () => 'CanViewCalculationTemplate',
    }),

    'DELETE /flexible-calculation/templates/:id': endpoint({
        description: 'Delete a calculation template',
        pathParams: z.object({
            id: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.union([z.undefined(), z.literal('')]),
        invalidates: ['GET /flexible-calculation/templates', 'GET /flexible-calculation/templates/:id'],
        actionPermission: () => 'CanDeleteCalculationTemplate',
    }),

    'POST /flexible-calculation/templates/convert-from-legacy/:id': endpoint({
        description: 'Convert a legacy calculation template to a flexible calculation template',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ currency: CurrencyRuntype }),
        responseBody: FlexibleCalculationTemplateRuntype,
        invalidates: ['GET /flexible-calculation/templates', 'GET /flexible-calculation/templates/:id'],
        actionPermission: () => 'CanCreateCalculationTemplate',
    }),

    'POST /flexible-calculation/templates/convert-all-from-legacy': endpoint({
        description: 'Convert all legacy calculation templates to flexible calculation templates',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ currency: CurrencyRuntype }),
        responseBody: z.array(FlexibleCalculationTemplateRuntype),
        invalidates: ['GET /flexible-calculation/templates', 'GET /flexible-calculation/templates/:id'],
        actionPermission: () => 'CanCreateCalculationTemplate',
    }),
    // Exported variable REST API endpoints
    'POST /flexible-calculation/exported-variables': endpoint({
        description: 'Create a new exported variable',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: ExportedVariableInputRuntype,
        responseBody: ExportedVariableRuntype,
        invalidates: ['GET /flexible-calculation/exported-variables'],
        actionPermission: () => 'CanCreateCalculationTemplate',
    }),

    'GET /flexible-calculation/exported-variables': endpoint({
        description: 'Retrieve all exported variables',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(ExportedVariableRuntype),
        actionPermission: () => 'CanViewCalculationTemplate',
    }),

    'GET /flexible-calculation/exported-variables/:id': endpoint({
        description: 'Retrieve an exported variable by ID',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: ExportedVariableRuntype,
        actionPermission: () => 'CanViewCalculationTemplate',
    }),

    'PATCH /flexible-calculation/exported-variables/:id': endpoint({
        description: 'Update an exported variable',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: ExportedVariableUpdateRuntype,
        responseBody: ExportedVariableRuntype,
        invalidates: [
            'GET /flexible-calculation/exported-variables',
            'GET /flexible-calculation/exported-variables/:id',
        ],
        actionPermission: () => 'CanUpdateCalculationTemplate',
    }),

    'DELETE /flexible-calculation/exported-variables/:id': endpoint({
        description: 'Delete an exported variable',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.union([z.undefined(), z.literal('')]),
        invalidates: [
            'GET /flexible-calculation/exported-variables',
            'GET /flexible-calculation/exported-variables/:id',
        ],
        actionPermission: () => 'CanDeleteCalculationTemplate',
    }),
};
